import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import { Plus, Trash2, Copy, Search, ChevronDown, Check, X } from 'lucide-react';
import axios from 'axios';
import { setupAxios } from '../../auth';
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

const DEFAULT_PAYMENT_TERMS = [
  { label: 'Due end of next month' },
  { label: 'Due end of the month' },
  { label: 'Due on Receipt', dueAfterDays: 0 },
  { label: 'Net 15', dueAfterDays: 15 },
  { label: 'Net 30', dueAfterDays: 30 },
  { label: 'Net 45', dueAfterDays: 45 },
  { label: 'Net 60', dueAfterDays: 60 },
];

const createEmptyContact = (id: number) => ({
  id,
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobile: '',
  skype: '',
  designation: '',
  department: '',
  showExtra: false,
});

const CustomSelect = ({ name, options, value, onChange, placeholder, className, containerClassName }: { name?: string; options: any[]; value?: any; onChange?: (val: any) => void; placeholder?: string; className?: string; containerClassName?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');
  const [searchQuery, setSearchQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const currentValue = onChange ? value : internalValue;
  const handleSelect = (val) => {
    if (onChange) onChange(val);
    else setInternalValue(val);
    setIsOpen(false);
    setSearchQuery('');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(o => String(o.label).toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className={`relative ${containerClassName || ''}`} ref={ref}>
      <input type="hidden" name={name} value={currentValue} />
      <div 
        className={`w-full px-3.5 py-2.5 bg-white border text-sm outline-none transition-all cursor-pointer flex justify-between items-center ${isOpen ? 'border-blue-400 ring-2 ring-blue-100 rounded-t-xl' : 'border-slate-200 hover:border-slate-300 rounded-xl'} ${className || ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={currentValue ? 'text-slate-800' : 'text-slate-400'}>
          {currentValue ? (options.find(o => o.value === currentValue)?.label || currentValue) : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      {isOpen && (
        <div className="absolute z-50 w-full bg-white border-x border-b border-blue-400 rounded-b-xl shadow-lg overflow-hidden flex flex-col max-h-60">
          {options.length > 10 && (
            <div className="p-2 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
                <Search className="w-3.5 h-3.5 text-slate-400" />
                <input 
                  type="text" 
                  placeholder="Search..." 
                  className="w-full bg-transparent text-sm outline-none"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onClick={e => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {placeholder && !searchQuery && (
              <div 
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${currentValue === '' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                onClick={() => handleSelect('')}
              >
                {placeholder}
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500 text-center">No results found</div>
            ) : (
              filteredOptions.map((opt, i) => (
                <div 
                  key={i}
                  className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${currentValue === opt.value ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AccountsNewCustomer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const customerId = id ? Number(id) : null;
  const isEditing = customerId !== null && !Number.isNaN(customerId);
  
  const [activeTab, setActiveTab] = useState('Other Details');
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [customerData, setCustomerData] = useState<any>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // States
  const [customerType, setCustomerType] = useState('Business');
  const [contactPersons, setContactPersons] = useState([createEmptyContact(1)]);
  const [nextContactId, setNextContactId] = useState(2);
  
  // Addresses
  const [billingCountry, setBillingCountry] = useState('IN');
  const [billingState, setBillingState] = useState('');
  const [shippingCountry, setShippingCountry] = useState('IN');
  const [shippingState, setShippingState] = useState('');

  // Payment Terms Modal
  const [paymentTerms, setPaymentTerms] = useState(DEFAULT_PAYMENT_TERMS);
  const [paymentTermValue, setPaymentTermValue] = useState('Due on Receipt');
  const [paymentTermOpen, setPaymentTermOpen] = useState(false);
  const [paymentTermQuery, setPaymentTermQuery] = useState('');
  const [showPaymentTermModal, setShowPaymentTermModal] = useState(false);
  const [newTermName, setNewTermName] = useState('');
  const [newTermDays, setNewTermDays] = useState('');
  const paymentTermRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && customerId) {
      setIsLoading(true);
      api.get(`/accounts/customers/${customerId}`)
        .then(res => {
          const data = res.data?.data || res.data;
          setCustomerData(data);
          
          if (data.customerType) setCustomerType(data.customerType);
          if (data.contacts && data.contacts.length > 0) {
            setContactPersons(data.contacts.map((c: any, i: number) => ({ ...createEmptyContact(i + 1), ...c, id: i + 1 })));
            setNextContactId(data.contacts.length + 1);
          }
          if (data.address) {
            if (data.address.billingCountry) setBillingCountry(data.address.billingCountry);
            if (data.address.billingState) setBillingState(data.address.billingState);
            if (data.address.shippingCountry) setShippingCountry(data.address.shippingCountry);
            if (data.address.shippingState) setShippingState(data.address.shippingState);
          }
          if (data.otherDetails && data.otherDetails.paymentTerms) {
            setPaymentTermValue(data.otherDetails.paymentTerms);
          }
        })
        .catch(err => {
          console.error("Failed to fetch customer", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isEditing, customerId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (paymentTermRef.current && !paymentTermRef.current.contains(event.target)) {
        setPaymentTermOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formRef.current) return;
    
    setSaving(true);
    try {
      const fd = new FormData(formRef.current);
      const payload: any = Object.fromEntries(fd.entries());
      
      // Validate primary email
      if (payload.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(payload.email)) {
          alert('Please enter a valid email address.');
          setSaving(false);
          return;
        }
      }

      // Add payment terms from state
      payload.paymentTerms = paymentTermValue;
      
      // Filter out completely empty contacts
      payload.contacts = contactPersons.filter(c => 
        c.firstName || c.lastName || c.email || c.phone || c.mobile || c.salutation
      );

      // Validate contact emails
      for (const contact of payload.contacts) {
        if (contact.email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(contact.email)) {
            alert(`Please enter a valid email address for contact: ${contact.firstName || ''} ${contact.lastName || ''}`);
            setSaving(false);
            return;
          }
        }
      }

      if (isEditing) {
        await api.put(`/accounts/customers/${customerId}`, payload);
      } else {
        await api.post('/accounts/customers', payload);
      }
      navigate('/accounts/sales/customers');
    } catch (err: any) { 
      console.error('Save failed:', err);
      alert('Failed to save customer');
    } finally { 
      setSaving(false); 
    }
  };

  const updateContact = (id: number, field: string, value: string) => {
    let cleanVal = value;
    if (field === 'phone' || field === 'mobile') {
      cleanVal = value.replace(/\D/g, '').slice(0, 10);
    }
    setContactPersons(prev => prev.map(c => c.id === id ? { ...c, [field]: cleanVal } : c));
  };

  const addContactPerson = () => {
    setContactPersons([...contactPersons, createEmptyContact(nextContactId)]);
    setNextContactId(prev => prev + 1);
  };

  const removeContactPerson = (idToRemove) => {
    setContactPersons(contactPersons.filter(c => c.id !== idToRemove));
  };

  const copyBillingToShipping = () => {
    if (!formRef.current) return;
    const form = formRef.current;
    
    const fields = ['Attention', 'Street1', 'Street2', 'City', 'State', 'Pincode', 'Phone', 'Fax'];
    fields.forEach(field => {
      const billingEl = form.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="billing${field}"]`);
      const shippingEl = form.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="shipping${field}"]`);
      if (billingEl && shippingEl) {
        shippingEl.value = billingEl.value;
      }
    });
    setShippingCountry(billingCountry);
    setTimeout(() => {
      setShippingState(billingState);
    }, 50);
  };

  const handleAddPaymentTerm = () => {
    if (!newTermName.trim()) return;
    const newTerm = { label: newTermName, dueAfterDays: Number(newTermDays) || 0 };
    setPaymentTerms([...paymentTerms, newTerm]);
    setPaymentTermValue(newTerm.label);
    setShowPaymentTermModal(false);
    setNewTermName('');
    setNewTermDays('');
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";
  const labelClass = "block text-[11px] font-bold text-slate-600 mb-1.5";

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              
              {/* Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts')}>ACCOUNTS</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts/sales/customers')}>SALES</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-blue-600">CUSTOMERS</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">{isEditing ? 'Edit Customer' : 'New Customer'}</h1>
                  <p className="text-sm font-medium text-slate-500 mt-1">{isEditing ? 'Update the customer details below' : 'Enter the customer details below'}</p>
                </div>
              </div>

              {isLoading ? (
                <div className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-16 flex items-center justify-center">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-500 font-medium">Loading customer data...</p>
                  </div>
                </div>
              ) : (
              <form key={customerData ? 'loaded' : 'new'} ref={formRef} onSubmit={handleSave} className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8">
                
                {/* Primary Details Section */}
                <div className="mb-8 max-w-4xl space-y-6">
                  {/* Customer Type */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Type</label>
                    </div>
                    <div className="flex-1 flex items-center gap-6">
                      <label className="flex items-center gap-2 text-sm cursor-pointer group">
                        <input type="radio" name="customerType" value="Business" checked={customerType === 'Business'} onChange={() => setCustomerType('Business')} className="w-4 h-4 text-blue-600 accent-blue-600 border-slate-300 focus:ring-blue-500" /> 
                        <span className="group-hover:text-blue-600 text-slate-800 transition-colors">Business</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer group">
                        <input type="radio" name="customerType" value="Individual" checked={customerType === 'Individual'} onChange={() => setCustomerType('Individual')} className="w-4 h-4 text-blue-600 accent-blue-600 border-slate-300 focus:ring-blue-500" /> 
                        <span className="group-hover:text-blue-600 text-slate-800 transition-colors">Individual</span>
                      </label>
                    </div>
                  </div>

                  {/* Primary Contact */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Primary Contact</label>
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-3">
                        <CustomSelect 
                          name="salutation"
                          value={customerData?.salutation || ''}
                          placeholder="Salutation"
                          options={[
                            { value: 'Mr.', label: 'Mr.' },
                            { value: 'Mrs.', label: 'Mrs.' },
                            { value: 'Ms.', label: 'Ms.' },
                            { value: 'Miss.', label: 'Miss.' },
                            { value: 'Dr.', label: 'Dr.' }
                          ]}
                        />
                      </div>
                      <div className="md:col-span-4">
                        <input type="text" name="firstName" defaultValue={customerData?.firstName || ''} placeholder="First Name" className={inputClass} />
                      </div>
                      <div className="md:col-span-5">
                        <input type="text" name="lastName" defaultValue={customerData?.lastName || ''} placeholder="Last Name" className={inputClass} />
                      </div>
                    </div>
                  </div>

                  {/* Company Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Company Name</label>
                    </div>
                    <div className="flex-1">
                      <input type="text" name="companyName" defaultValue={customerData?.companyName || ''} placeholder="Company Name" className={inputClass} />
                    </div>
                  </div>

                  {/* Display Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Display Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="text" name="displayName" defaultValue={customerData?.displayName || ''} required placeholder="Type to add" className={inputClass} />
                    </div>
                  </div>

                  {/* Email Address */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Email Address</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1 relative">
                      <div className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
                      </div>
                      <input type="email" name="email" required defaultValue={customerData?.email || ''} placeholder="Email Address" className={`${inputClass} pl-10`} />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Phone</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1 space-y-4 max-w-lg">
                      <div className="flex items-center gap-4">
                        <CustomSelect 
                          name="workPhoneCode"
                          placeholder="+91"
                          options={[
                            { value: '+91', label: '+91' },
                            { value: '+1', label: '+1' },
                            { value: '+44', label: '+44' },
                            { value: '+65', label: '+65' },
                            { value: '+971', label: '+971' },
                            { value: '+61', label: '+61' },
                            { value: '+81', label: '+81' },
                            { value: '+49', label: '+49' },
                            { value: '+33', label: '+33' },
                            { value: '+60', label: '+60' },
                            { value: '+852', label: '+852' },
                            { value: '+31', label: '+31' },
                            { value: '+39', label: '+39' },
                            { value: '+34', label: '+34' },
                            { value: '+966', label: '+966' },
                            { value: '+974', label: '+974' },
                            { value: '+965', label: '+965' }
                          ]}
                          containerClassName="w-[100px]"
                        />
                        <input 
                          type="text" 
                          name="workPhone" 
                          required
                          defaultValue={customerData?.workPhone || ''} 
                          placeholder="Work Phone" 
                          maxLength={10}
                          onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)}
                          className={`${inputClass} flex-1`} 
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <CustomSelect 
                          name="mobileCode"
                          placeholder="+91"
                          options={[
                            { value: '+91', label: '+91' },
                            { value: '+1', label: '+1' },
                            { value: '+44', label: '+44' },
                            { value: '+65', label: '+65' },
                            { value: '+971', label: '+971' },
                            { value: '+61', label: '+61' },
                            { value: '+81', label: '+81' },
                            { value: '+49', label: '+49' },
                            { value: '+33', label: '+33' },
                            { value: '+60', label: '+60' },
                            { value: '+852', label: '+852' },
                            { value: '+31', label: '+31' },
                            { value: '+39', label: '+39' },
                            { value: '+34', label: '+34' },
                            { value: '+966', label: '+966' },
                            { value: '+974', label: '+974' },
                            { value: '+965', label: '+965' }
                          ]}
                          containerClassName="w-[100px]"
                        />
                        <input 
                          type="text" 
                          name="mobile" 
                          defaultValue={customerData?.mobile || ''} 
                          placeholder="Mobile" 
                          maxLength={10}
                          onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)}
                          className={`${inputClass} flex-1`} 
                        />
                      </div>
                    </div>
                  </div>

                  {/* Customer Language */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Language</label>
                    </div>
                    <div className="flex-1">
                      <CustomSelect 
                        name="language"
                        placeholder="Select Language"
                        value={customerData?.language || 'English'}
                        options={[
                          { value: 'English', label: 'English' },
                          { value: 'Spanish', label: 'Spanish' },
                          { value: 'French', label: 'French' },
                          { value: 'Arabic', label: 'Arabic' },
                          { value: 'German', label: 'German' },
                          { value: 'Chinese', label: 'Chinese (Mandarin)' },
                          { value: 'Japanese', label: 'Japanese' },
                          { value: 'Hindi', label: 'Hindi' },
                          { value: 'Portuguese', label: 'Portuguese' },
                          { value: 'Russian', label: 'Russian' },
                          { value: 'Italian', label: 'Italian' },
                          { value: 'Dutch', label: 'Dutch' },
                          { value: 'Turkish', label: 'Turkish' },
                          { value: 'Malay', label: 'Malay' }
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Tabs Navigation */}
                <div className="flex overflow-visible border-slate-100 gap-2 mb-8">
                  {['Other Details', 'Address', 'Contact Persons','Remarks'].map(tab => (
                    <button 
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`px-5 py-3 text-sm font-bold transition-all whitespace-nowrap border-b-2 -mb-px ${activeTab === tab ? 'text-blue-600 border-blue-600' : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-200'}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Tab Contents */}
                <div className="min-h-75">
                  
                  <div className={activeTab === 'Other Details' ? 'grid grid-cols-1 md:grid-cols-2 gap-8' : 'hidden'}>
                    <div className="space-y-6">
                      <div>
                        <label className={labelClass}>PAN Card</label>
                        <input 
                          type="text" 
                          name="pan" 
                          defaultValue={customerData?.otherDetails?.pan || ''} 
                          placeholder="ABCDE1234F" 
                          maxLength={10}
                          pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                          title="PAN must be 5 letters, 4 digits, and 1 letter (e.g., ABCDE1234F)"
                          onInput={e => e.currentTarget.value = e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, '')}
                          className={inputClass} 
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Currency</label>
                          <CustomSelect 
                            name="currency" 
                            value={customerData?.otherDetails?.currency || 'INR'}
                            options={[
                              { value: 'INR', label: 'INR - Indian Rupee' },
                              { value: 'USD', label: 'USD - US Dollar' },
                              { value: 'EUR', label: 'EUR - Euro' },
                              { value: 'GBP', label: 'GBP - British Pound' },
                              { value: 'AED', label: 'AED - UAE Dirham' },
                              { value: 'SGD', label: 'SGD - Singapore Dollar' },
                              { value: 'AUD', label: 'AUD - Australian Dollar' },
                              { value: 'CAD', label: 'CAD - Canadian Dollar' },
                              { value: 'JPY', label: 'JPY - Japanese Yen' },
                              { value: 'CHF', label: 'CHF - Swiss Franc' },
                              { value: 'CNY', label: 'CNY - Chinese Yuan' },
                              { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
                              { value: 'MYR', label: 'MYR - Malaysian Ringgit' },
                              { value: 'SAR', label: 'SAR - Saudi Riyal' },
                              { value: 'QAR', label: 'QAR - Qatari Riyal' }
                            ]}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Opening Balance</label>
                          <input 
                            type="number" 
                            name="openingBalance" 
                            defaultValue={customerData?.otherDetails?.openingBalance || ''} 
                            step="0.01" 
                            min="0"
                            onKeyDown={e => {
                              if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                              }
                            }}
                            placeholder="0.00" 
                            className={inputClass} 
                          />
                        </div>
                        <div ref={paymentTermRef}>
                          <label className={labelClass}>Payment Terms</label>
                          <div className="relative">
                            <div 
                              className={`${inputClass} flex items-center justify-between cursor-pointer`}
                              onClick={() => setPaymentTermOpen(!paymentTermOpen)}
                            >
                              <span>{paymentTermValue}</span>
                              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${paymentTermOpen ? 'rotate-180' : ''}`} />
                            </div>
                            
                            {paymentTermOpen && (
                              <div className="absolute z-50 w-full bottom-full mb-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                                <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                                  <Search className="w-4 h-4 text-slate-400" />
                                  <input 
                                    type="text" 
                                    placeholder="Search terms..." 
                                    className="w-full text-sm outline-none"
                                    value={paymentTermQuery}
                                    onChange={e => setPaymentTermQuery(e.target.value)}
                                  />
                                </div>
                                <div className="max-h-48 overflow-y-auto py-1">
                                  {paymentTerms.filter(t => t.label.toLowerCase().includes(paymentTermQuery.toLowerCase())).map((term, i) => (
                                    <div 
                                      key={i} 
                                      className={`px-4 py-2 text-sm cursor-pointer hover:bg-slate-50 flex items-center justify-between ${paymentTermValue === term.label ? 'text-blue-600 font-semibold bg-blue-50/50' : 'text-slate-700'}`}
                                      onClick={() => { setPaymentTermValue(term.label); setPaymentTermOpen(false); }}
                                    >
                                      {term.label}
                                      {paymentTermValue === term.label && <Check className="w-4 h-4" />}
                                    </div>
                                  ))}
                                </div>
                                <div 
                                  className="p-3 border-t border-slate-100 bg-slate-50/50 text-blue-600 text-sm font-semibold cursor-pointer hover:bg-blue-50 flex items-center gap-2 transition-colors"
                                  onClick={() => { setPaymentTermOpen(false); setShowPaymentTermModal(true); }}
                                >
                                  <Plus className="w-4 h-4" /> Add New Payment Term
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <label className={labelClass}>Website</label>
                          <input type="text" name="website" defaultValue={customerData?.otherDetails?.website || ''} placeholder="www.example.com" className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>Department</label>
                          <input type="text" name="department" defaultValue={customerData?.otherDetails?.department || ''} placeholder="Department" className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>Designation</label>
                          <input type="text" name="designation" defaultValue={customerData?.otherDetails?.designation || ''} placeholder="Designation" className={inputClass} />
                        </div>
                      </div>
                    </div>

                    <div className={activeTab === 'Address' ? 'grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8' : 'hidden'}>
                      {/* Billing Address */}
                      <div>
                        <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                          <span className="w-1.5 h-4 bg-blue-500 rounded-full"></span> Billing Address
                        </h3>
                        <div className="space-y-4">
                          <div><label className={labelClass}>Attention</label><input type="text" name="billingAttention" defaultValue={customerData?.address?.billingAttention || ''} className={inputClass} /></div>
                          <div>
                            <label className={labelClass}>Country</label>
                            <CustomSelect 
                              name="billingCountry"
                              placeholder="Select Country"
                              value={billingCountry}
                              onChange={val => { setBillingCountry(val); setBillingState(''); }}
                              options={Country.getAllCountries().map(c => ({ value: c.isoCode, label: c.name }))}
                            />
                          </div>
                          <div><label className={labelClass}>Address</label><textarea name="billingStreet1" defaultValue={customerData?.address?.billingStreet1 || ''} rows={2} className={`${inputClass} resize-none`} placeholder="Street 1"></textarea></div>
                          <div><textarea name="billingStreet2" defaultValue={customerData?.address?.billingStreet2 || ''} rows={2} className={`${inputClass} resize-none`} placeholder="Street 2"></textarea></div>
                          <div><label className={labelClass}>City</label><input type="text" name="billingCity" defaultValue={customerData?.address?.billingCity || ''} className={inputClass} /></div>
                          <div>
                            <label className={labelClass}>State</label>
                            {billingCountry ? (
                              <CustomSelect 
                                name="billingState"
                                placeholder="Select State"
                                value={billingState}
                                onChange={val => setBillingState(val)}
                                options={State.getStatesOfCountry(billingCountry).map(s => ({ value: s.isoCode, label: s.name }))}
                              />
                            ) : (
                              <input type="text" name="billingState" defaultValue={customerData?.address?.billingState || ''} className={inputClass} />
                            )}
                          </div>
                          <div><label className={labelClass}>Zip Code</label><input type="text" name="billingPincode" defaultValue={customerData?.address?.billingPincode || ''} maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10)} className={inputClass} /></div>
                          <div><label className={labelClass}>Phone</label><input type="text" name="billingPhone" defaultValue={customerData?.address?.billingPhone || ''} maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} className={inputClass} /></div>
                          <div><label className={labelClass}>Fax</label><input type="text" name="billingFax" defaultValue={customerData?.address?.billingFax || ''} maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} className={inputClass} /></div>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <span className="w-1.5 h-4 bg-emerald-500 rounded-full"></span> Shipping Address
                          </h3>
                          <button type="button" onClick={copyBillingToShipping} className="text-[11px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 px-2 py-1 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                            <Copy className="w-3.5 h-3.5" /> Copy billing address
                          </button>
                        </div>
                        <div className="space-y-4">
                          <div><label className={labelClass}>Attention</label><input type="text" name="shippingAttention" defaultValue={customerData?.address?.shippingAttention || ''} className={inputClass} /></div>
                          <div>
                            <label className={labelClass}>Country</label>
                            <CustomSelect 
                              name="shippingCountry"
                              placeholder="Select Country"
                              value={shippingCountry}
                              onChange={val => { setShippingCountry(val); setShippingState(''); }}
                              options={Country.getAllCountries().map(c => ({ value: c.isoCode, label: c.name }))}
                            />
                          </div>
                          <div><label className={labelClass}>Address</label><textarea name="shippingStreet1" defaultValue={customerData?.address?.shippingStreet1 || ''} rows={2} className={`${inputClass} resize-none`} placeholder="Street 1"></textarea></div>
                          <div><textarea name="shippingStreet2" defaultValue={customerData?.address?.shippingStreet2 || ''} rows={2} className={`${inputClass} resize-none`} placeholder="Street 2"></textarea></div>
                          <div><label className={labelClass}>City</label><input type="text" name="shippingCity" defaultValue={customerData?.address?.shippingCity || ''} className={inputClass} /></div>
                          <div>
                            <label className={labelClass}>State</label>
                            {shippingCountry ? (
                              <CustomSelect 
                                name="shippingState"
                                placeholder="Select State"
                                value={shippingState}
                                onChange={val => setShippingState(val)}
                                options={State.getStatesOfCountry(shippingCountry).map(s => ({ value: s.isoCode, label: s.name }))}
                              />
                            ) : (
                              <input type="text" name="shippingState" defaultValue={customerData?.address?.shippingState || ''} className={inputClass} />
                            )}
                          </div>
                          <div><label className={labelClass}>Zip Code</label><input type="text" name="shippingPincode" defaultValue={customerData?.address?.shippingPincode || ''} maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10)} className={inputClass} /></div>
                          <div><label className={labelClass}>Phone</label><input type="text" name="shippingPhone" defaultValue={customerData?.address?.shippingPhone || ''} maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} className={inputClass} /></div>
                          <div><label className={labelClass}>Fax</label><input type="text" name="shippingFax" defaultValue={customerData?.address?.shippingFax || ''} maxLength={10} onInput={e => e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 10)} className={inputClass} /></div>
                        </div>
                      </div>
                    </div>

                    <div className={activeTab === 'Contact Persons' ? 'overflow-visible' : 'hidden'}>
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 text-xs font-bold uppercase tracking-wider">
                            <th className="p-3 rounded-tl-xl">Salutation</th>
                            <th className="p-3">First Name</th>
                            <th className="p-3">Last Name</th>
                            <th className="p-3">Email Address</th>
                            <th className="p-3">Work Phone</th>
                            <th className="p-3">Mobile</th>
                            <th className="p-3 rounded-tr-xl w-12 text-center"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {contactPersons.map((contact, idx) => (
                            <tr key={contact.id} className="hover:bg-slate-50/50 transition-colors">
                              <td className="p-3">
                                <CustomSelect 
                                  placeholder="Select"
                                  value={contact.salutation}
                                  onChange={val => updateContact(contact.id, 'salutation', val)}
                                  options={[
                                    { value: 'Mr.', label: 'Mr.' },
                                    { value: 'Mrs.', label: 'Mrs.' },
                                    { value: 'Ms.', label: 'Ms.' },
                                    { value: 'Miss.', label: 'Miss.' },
                                    { value: 'Dr.', label: 'Dr.' }
                                  ]}
                                />
                              </td>
                              <td className="p-3"><input type="text" value={contact.firstName} onChange={e => updateContact(contact.id, 'firstName', e.target.value)} placeholder="First Name" className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" /></td>
                              <td className="p-3"><input type="text" value={contact.lastName} onChange={e => updateContact(contact.id, 'lastName', e.target.value)} placeholder="Last Name" className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" /></td>
                              <td className="p-3"><input type="email" value={contact.email} onChange={e => updateContact(contact.id, 'email', e.target.value)} placeholder="Email" className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" /></td>
                              <td className="p-3"><input type="text" value={contact.phone} maxLength={10} onChange={e => updateContact(contact.id, 'phone', e.target.value)} placeholder="Phone" className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" /></td>
                              <td className="p-3"><input type="text" value={contact.mobile} maxLength={10} onChange={e => updateContact(contact.id, 'mobile', e.target.value)} placeholder="Mobile" className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400" /></td>
                              <td className="p-3 text-center">
                                {idx > 0 && (
                                  <button type="button" onClick={() => removeContactPerson(contact.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button type="button" onClick={addContactPerson} className="mt-4 flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-xl transition-colors">
                        <Plus className="w-4 h-4" /> Add Contact Person
                      </button>
                    </div>

                    <div className={activeTab === 'Custom Fields' ? 'flex flex-col items-center justify-center py-16 text-center' : 'hidden'}>
                      <div className="w-16 h-16 bg-blue-50 text-blue-400 rounded-full flex items-center justify-center mb-4">
                        <i data-lucide="layers" className="w-8 h-8"></i>
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">No Custom Fields</h3>
                      <p className="text-sm text-slate-500 max-w-sm">You haven't configured any custom fields for customers yet. You can set them up in settings.</p>
                    </div>

                    <div className={activeTab === 'Reporting Tags' ? 'flex flex-col items-center justify-center py-16 text-center' : 'hidden'}>
                      <div className="w-16 h-16 bg-blue-50 text-blue-400 rounded-full flex items-center justify-center mb-4">
                        <i data-lucide="tag" className="w-8 h-8"></i>
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">No Reporting Tags</h3>
                      <p className="text-sm text-slate-500 max-w-sm">Associate reporting tags with customers to track transactions easily.</p>
                    </div>

                    <div className={activeTab === 'Remarks' ? 'block' : 'hidden'}>
                      <label className={labelClass}>Remarks (For Internal Use)</label>
                      <textarea name="remarks" defaultValue={customerData?.remarks || ''} rows={6} className={`${inputClass} resize-none`} placeholder="Add any additional notes about this customer..."></textarea>
                    </div>
                  </div>

                {/* Footer Actions */}
                <div className="pt-8 mt-8 border-t border-slate-100 flex items-center gap-3">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Customer'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:shadow transition-all hover:bg-slate-50">
                    Cancel
                  </button>
                </div>
              </form>
              )}
            </div>
            
          </div>
        </main>
      </div>

      {/* Payment Term Modal */}
      {showPaymentTermModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Add New Payment Term</h3>
              <button onClick={() => setShowPaymentTermModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={labelClass}>Term Name <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={newTermName} 
                  onChange={e => setNewTermName(e.target.value)}
                  placeholder="e.g. Net 90" 
                  className={inputClass} 
                  autoFocus 
                />
              </div>
              <div>
                <label className={labelClass}>Number of Days <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  value={newTermDays} 
                  onChange={e => setNewTermDays(e.target.value)}
                  placeholder="e.g. 90" 
                  className={inputClass} 
                />
                <p className="text-[11px] text-slate-500 mt-1.5">Enter the number of days after the invoice date the payment is due.</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button onClick={() => setShowPaymentTermModal(false)} className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200 bg-slate-100 rounded-xl transition-colors">Cancel</button>
              <button onClick={handleAddPaymentTerm} disabled={!newTermName || !newTermDays} className="px-5 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl shadow-md disabled:opacity-50 transition-all hover:bg-blue-700">Save Term</button>
            </div>
          </div>
        </div>
      )}

      
    </>
  );
}

